import { Coordinates, PageItems, Point } from '@/types';
import { isNetworkFailure } from '@/services/errorReporter';
import { isAxiosError } from 'axios';
import i18next, { t } from 'i18next';
import { MAX_INSERTED_IMAGE_SIZE } from '../components/PdfEditor/constants.ts';

/**
 * Remove the file extension from a filename.
 *
 * @param fileName The filename to remove the extension from.
 * @returns The filename without the extension.
 */
export const removeFileExtension = (fileName: string) => fileName.replace(/\.[^/.]+$/, '');

/**
 * Move the cursor to the start of a content editable div.
 *
 * @param contentEle The content editable div.
 */
export const moveCursorToStart = (contentEle: HTMLDivElement) => {
  contentEle.focus();
  const range = document.createRange();
  const selection = window.getSelection();
  range.setStart(contentEle, 0);
  range.collapse(true);
  selection?.removeAllRanges();
  selection?.addRange(range);
};

export const loadFileAsURL = (file: File) => {
  const reader = new FileReader();

  reader.onload = function () {
    // do something with the result
    //var file = reader.result || e.target.result;
  };
  reader.readAsDataURL(file);
};

export const fileToBlob = (file: File) => {
  return new Promise<Blob>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => {
      if (reader.result === null) return reject('File is empty');
      if (reader.result instanceof ArrayBuffer) {
        const blob = new Blob([new Uint8Array(reader.result)], { type: file.type });
        resolve(blob);
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };
  });
};

export const fileToArrayBuffer = (file: File) => {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) return resolve(reader.result);
      reject('File is empty');
    };
    reader.onerror = (error) => reject(error);
  });
};

// Rebuilds an object URL from an IndexedDB image record. Records are persisted as an
// ArrayBuffer + MIME type, but tolerate legacy records that stored a Blob directly.
// TODO(deprecate): drop the legacy `blob` fallback once old records have aged out.
export const dbImageToObjectUrl = (record: { buffer?: ArrayBuffer; type?: string; blob?: Blob }): string => {
  const blob = record.buffer ? new Blob([record.buffer], { type: record.type }) : record.blob;
  if (!blob) throw new Error('Image record has no data');
  return URL.createObjectURL(blob);
};

export const objectURLToFile = async (url: string) => {
  const blob = await (await fetch(url)).blob();
  return blob;
  //return new File(blob, "image");
};

export function dataURLtoBlob(data_url: string) {
  const bstr = atob(data_url);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr]);
}

/**
 * Extract the backend error code (the `message` field of an API error response,
 * e.g. "FILE_IS_DELETED") from a caught error, or '' when there isn't one.
 */
export const getBackendErrorCode = (error: unknown): string =>
  isAxiosError(error) ? String(error.response?.data?.message ?? '') : '';

/**
 * A "known" error is one the backend reported with a code we have a translation
 * for in the `back` namespace — i.e. an expected, user-facing condition rather
 * than an unexpected app fault. Used to show a specific message and to skip
 * Sentry reporting for these.
 */
export const isKnownBackendError = (error: unknown): boolean => {
  const code = getBackendErrorCode(error);
  return !!code && i18next.exists(`back:${code}`);
};

export function getResponseError(e: unknown) {
  if (isAxiosError(e)) {
    // No response at all means there's no backend code to translate, and axios' own
    // "Network Error" text is untranslated — use our localized copy instead. (This
    // branch used to fall through to `t('back:undefined')`, so users were literally
    // shown the word "undefined".)
    if (isNetworkFailure(e) || !e.response) {
      return t('web:common.networkError');
    }
    const code = getBackendErrorCode(e);
    return code ? t(`back:${code}`) : e.message;
  } else if (e instanceof Error) {
    return e.message;
  } else {
    return t('web:common.error');
  }
}

export const getImageMetadata = (url: string) => {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = url;
  });
};

export const computeInsertedImageSize = (
  loadedImg: HTMLImageElement,
  pagesMetadata: PageItems,
  scale: number,
  viewportWidth: number | undefined,
) => {
  const currentPageWidth = pagesMetadata[0].page.getViewport({ scale }).width;
  const maxWidth = Math.min(currentPageWidth * 0.9, (viewportWidth ?? Infinity) * 0.9, MAX_INSERTED_IMAGE_SIZE);
  const maxHeight = MAX_INSERTED_IMAGE_SIZE;
  const fitRatio = Math.min(1, maxWidth / loadedImg.naturalWidth, maxHeight / loadedImg.naturalHeight);
  const width = loadedImg.naturalWidth * fitRatio;
  const height = loadedImg.naturalHeight * fitRatio;
  return { width, height };
};

export const rotateImage = (url: string, degrees: number) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  return new Promise<string>((resolve, reject) => {
    const img = new Image();
    img.onload = function () {
      // Set canvas dimensions to match image dimensions
      canvas.width = img.height; //TODO: Calculate new width based on the rotated width
      canvas.height = img.width; //TODO: Calculate new height based on the rotated height
      if (!ctx) return reject('Canvas context is null');

      // Translate and rotate the context
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((degrees * Math.PI) / 180);

      // Set image smoothing quality
      ctx.imageSmoothingQuality = 'high';

      // Draw the rotated image
      ctx.drawImage(img, -img.width / 2, -img.height / 2);

      // Get the rotated image data URL
      canvas.toBlob((blob) => (blob ? resolve(URL.createObjectURL(blob)) : reject(t('common.createBlobError'))));
    };
    img.src = url;
  });
};

export const transform = (m1: number[], m2: number[]) => {
  return [
    m1[0] * m2[0] + m1[2] * m2[1],
    m1[1] * m2[0] + m1[3] * m2[1],
    m1[0] * m2[2] + m1[2] * m2[3],
    m1[1] * m2[2] + m1[3] * m2[3],
    m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
    m1[1] * m2[4] + m1[3] * m2[5] + m1[5],
  ];
};

type TextImageOptions = {
  text: string;
  fontFamily: string;
  fontSize: number;
  color: string;
};
export const createTextImage = (context: TextImageOptions) => {
  const { text, fontFamily, fontSize, color } = context;
  return new Promise<File>((resolve, reject) => {
    // Create a canvas element
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Set the canvas size based on the text and font (optional)
    // You can estimate the size or use text metrics for better accuracy

    if (!ctx) {
      reject('Canvas context is null');
      return;
    }

    // Set the font style
    ctx.font = `${fontSize}px ${fontFamily}`;

    // Measure the text size
    const textMetrics = ctx.measureText(text);
    const textWidth = textMetrics.width;
    const textHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent;

    // Set canvas dimensions
    canvas.width = textWidth + 20; // Add some padding
    canvas.height = textHeight + 20; // Add some padding

    ctx.font = `${fontSize}px ${fontFamily}`;

    // Set text color (optional, defaults to black)
    ctx.fillStyle = color;

    // Draw the text on the canvas
    ctx.fillText(text, 10, textMetrics.actualBoundingBoxAscent + 10); // Adjust positioning as needed

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'signature.png', { type: 'image/png' });
        resolve(file);
      } else reject(t('common.createBlobError'));
    });
  });
};

export const bytesToMegabytes = (bytes: number) => Math.round((bytes / 1024 / 1024) * 100) / 100;

export const textItemToEditableTextBoxCoordinates = (coordinates: Coordinates) => {
  return {
    x: coordinates.x,
    y: coordinates.y - coordinates.height * 0.8,
    width: coordinates.width,
    height: coordinates.height,
  };
};

export const calculateDistance = (p1: Point, p2: Point) => {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
};

export const extractEmbeddedFontNumber = (fontName: string) => {
  const match = fontName.match(/f(\d+)$/);

  if (match) {
    const numberAfterF = match[1];
    return parseInt(numberAfterF);
  }

  return -1;
};

export const verifyEmbeddedFontFormat = (str: string) => {
  const regex = /^g_d\d+_f\d+$/;
  return regex.test(str);
};

export function isEnumMember<E>(value: unknown, enumArg: Record<string | number | symbol, E>): value is E {
  return (Object.values(enumArg) as unknown[]).includes(value);
}

export function degreesToRadians(angle: number) {
  return (angle * Math.PI) / 180;
}
